// v10/src/components/admin/fuel/FuelTankCard.js
//
// Card visual de un tanque de combustible.
// Muestra stock, %, barra, estado, mínimo, capacidad, notas.
// El kebab menu dispara onEdit(tank) / onDelete(tank) hacia FuelTab.

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Fuel, MoreVertical, Edit3, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Formato de números es-AR: separador de miles con punto, decimales con coma.
const formatLiters = (n, locale = 'es-AR') => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString(locale, { maximumFractionDigits: 0 });
};

// Mapa de estado → color/label.
const computeStatus = (current, minimum) => {
  if (current <= 0) return 'critical';
  if (current < minimum) return 'low';
  return 'ok';
};

const STATUS_COLORS = {
  ok:       { bar: '#16a34a', bg: 'rgba(22,163,74,0.12)',  text: '#16a34a' },
  low:      { bar: '#f59e0b', bg: 'rgba(245,158,11,0.12)', text: '#d97706' },
  critical: { bar: '#ef4444', bg: 'rgba(239,68,68,0.12)',  text: '#dc2626' },
};

const FuelTankCard = ({ tank, darkMode, canManage, onEdit, onDelete, locale = 'es-AR' }) => {
  const { t } = useTranslation('admin');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Cerrar el menú al click fuera.
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const capacity = Number(tank.capacity_liters)      || 0;
  const current  = Number(tank.current_stock_liters) || 0;
  const minimum  = Number(tank.minimum_stock_liters) || 0;
  const percent  = capacity > 0 ? Math.min(100, Math.round((current / capacity) * 100)) : 0;
  const status   = computeStatus(current, minimum);
  const colors   = STATUS_COLORS[status];

  const statusLabel =
    status === 'ok'       ? t('fuel_tank_status_ok')
    : status === 'low'    ? t('fuel_tank_status_low')
    :                       t('fuel_tank_status_critical');

  const cardBg     = darkMode ? 'rgba(255,255,255,0.03)' : 'white';
  const cardBorder = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0';
  const fgPrimary  = darkMode ? 'white' : '#0f172a';
  const fgMuted    = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';
  const fgFaint    = darkMode ? 'rgba(255,255,255,0.30)' : '#94a3b8';
  const trackBg    = darkMode ? 'rgba(255,255,255,0.07)' : '#f1f5f9';

  const handleEdit = () => {
    setMenuOpen(false);
    if (onEdit) onEdit(tank);
  };
  const handleDelete = () => {
    setMenuOpen(false);
    if (onDelete) onDelete(tank);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      style={{
        background: cardBg,
        border: cardBorder,
        borderRadius: '20px',
        padding: '20px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        position: 'relative',
        overflow: 'visible',
      }}
    >
      {/* Header: nombre + tipo + badge estado + kebab */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
        <div
          style={{
            width: 44, height: 44, borderRadius: 14,
            background: colors.bg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Fuel style={{ width: 22, height: 22, color: colors.bar }} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <h3
            style={{
              margin: 0, fontSize: '16px', fontWeight: 800, color: fgPrimary,
              letterSpacing: '-0.2px', lineHeight: 1.25,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}
          >
            {tank.name}
          </h3>
          <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span
              style={{
                fontSize: 11, fontWeight: 700,
                padding: '2px 8px', borderRadius: 999,
                background: darkMode ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.10)',
                color: darkMode ? '#a5b4fc' : '#4f46e5',
                letterSpacing: '0.2px',
              }}
            >
              {tank.fuel_type}
            </span>
          </div>
        </div>

        {/* Badge estado */}
        <span
          style={{
            fontSize: 11, fontWeight: 800,
            padding: '4px 10px', borderRadius: 999,
            background: colors.bg, color: colors.text,
            flexShrink: 0,
          }}
        >
          {statusLabel}
        </span>

        {/* Kebab menu — solo si el rol puede gestionar */}
        {canManage && (
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setMenuOpen((p) => !p)}
              aria-label={t('fuel_tank_actions_menu')}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                padding: 6, borderRadius: 10, display: 'flex',
                color: fgMuted,
              }}
            >
              <MoreVertical style={{ width: 18, height: 18 }} />
            </button>
            <AnimatePresence>
              {menuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.12 }}
                  style={{
                    position: 'absolute', top: 'calc(100% + 4px)', right: 0,
                    minWidth: 180,
                    background: darkMode ? '#1f2937' : 'white',
                    border: cardBorder,
                    borderRadius: 14, padding: 6,
                    boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
                    zIndex: 30,
                  }}
                >
                  <button
                    type="button"
                    onClick={handleEdit}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 12px', borderRadius: 10, border: 'none', cursor: 'pointer',
                      background: 'transparent', color: fgPrimary,
                      fontFamily: 'inherit', fontSize: 13, fontWeight: 600, textAlign: 'left',
                    }}
                  >
                    <Edit3 style={{ width: 15, height: 15 }} />
                    {t('fuel_action_edit')}
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 12px', borderRadius: 10, border: 'none', cursor: 'pointer',
                      background: 'transparent', color: '#ef4444',
                      fontFamily: 'inherit', fontSize: 13, fontWeight: 600, textAlign: 'left',
                    }}
                  >
                    <Trash2 style={{ width: 15, height: 15 }} />
                    {t('fuel_action_delete')}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Stock principal */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: fgFaint }}>
            {t('fuel_tank_current_stock')}
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 30, fontWeight: 900, color: fgPrimary, lineHeight: 1, letterSpacing: '-0.5px' }}>
            {formatLiters(current, locale)} <span style={{ fontSize: 16, fontWeight: 700, color: fgMuted }}>L</span>
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ margin: 0, fontSize: 24, fontWeight: 900, color: colors.text, lineHeight: 1 }}>
            {percent}%
          </p>
        </div>
      </div>

      {/* Barra de progreso */}
      <div
        style={{
          width: '100%', height: 10, borderRadius: 999,
          background: trackBg, overflow: 'hidden', marginBottom: 10,
        }}
      >
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percent}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          style={{
            height: '100%',
            background: colors.bar,
            borderRadius: 999,
          }}
        />
      </div>

      {/* Línea inferior: mínimo + capacidad */}
      <p style={{ margin: 0, fontSize: 12, color: fgMuted }}>
        {t('fuel_tank_minimum')}: <strong style={{ color: fgPrimary, fontWeight: 700 }}>{formatLiters(minimum, locale)} L</strong>
        {' · '}
        {t('fuel_tank_capacity')}: <strong style={{ color: fgPrimary, fontWeight: 700 }}>{formatLiters(capacity, locale)} L</strong>
      </p>

      {/* Notas opcionales */}
      {tank.notes && (
        <div
          style={{
            marginTop: 12, padding: '10px 12px',
            borderRadius: 12,
            background: darkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc',
            border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #eef2f7',
          }}
        >
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: fgFaint, marginBottom: 4 }}>
            {t('fuel_tank_notes_label')}
          </p>
          <p style={{ margin: 0, fontSize: 13, color: fgMuted, lineHeight: 1.45 }}>
            {tank.notes}
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default FuelTankCard;
